import { attr, FASTElement, observable } from "@microsoft/fast-element";
import { switchStyles } from "./switch.styles";
import { NatGenDesignSystem } from "../../natgen-design-system";
import { keyEnter, keySpace } from "@microsoft/fast-web-utilities";
import { CheckableFormAssociated } from "../../foundation/form-associated";
import { switchTemplate } from "./switch.template";

class _Switch extends FASTElement {}
interface _Switch extends CheckableFormAssociated {}

/**
 * A form-associated base class for the {@link @microsoft/fast-foundation#(FASTSwitch:class)} component.
 *
 * @beta
 */
export class FormAssociatedSwitch extends CheckableFormAssociated(_Switch) {
    proxy = document.createElement("input");
}

export class NatGenSwitch extends FormAssociatedSwitch {
    /**
         * When true, the control will be immutable by user interaction.
         * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute} for more information.
         * @public
         * @remarks
         * HTML Attribute: readonly
         */
    @attr({ attribute: "readonly", mode: "boolean" })
    public readOnly: boolean; // Map to proxy element
    protected readOnlyChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.readOnly = this.readOnly;
        }
    }

    /**
     * The element's value to be included in form submission when checked.
     * Default to "on" to reach parity with input[type="checkbox"]
     *
     * @internal
     */
    public initialValue: string = "on";

    /**
     * @internal
     */
    @observable
    public defaultSlottedNodes: Node[];

    public constructor() {
        super();

        this.proxy.setAttribute("type", "checkbox");
    }

    /**
     * @internal
     */
    public keypressHandler = (e: KeyboardEvent) => {
        if (this.readOnly) {
            return;
        }

        switch (e.key) {
            case keyEnter:
            case keySpace:
                this.checked = !this.checked;
                break;
        }
    };

    /**
     * @internal
     */
    public clickHandler = (e: MouseEvent) => {
        if (!this.disabled && !this.readOnly) {
            this.checked = !this.checked;
        }
    };
}

/**
 * @element natgen-switch
 */
export const natGenSwitchDefinition = NatGenSwitch.compose({
    name: `${NatGenDesignSystem.prefix}-switch`,
    template: switchTemplate({
        thumb: '<span class="checked-indicator" part="checked-indicator"></span>'
    }),
    styles: switchStyles
});
